import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import './theme.css';
import avatar from '../assets/profile.png';

interface ApiInfo {
  name: string;
  email: string;
  phone: string;
  links: {
    github: string;
    freelancer: string;
    linkedin: string;
    discord: {
      username: string;
      profile: string;
    };
  };
}

interface ProtectedRouteProps {
  isLoggedIn: boolean;
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ isLoggedIn, children }) => {
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<{ name: string; email: string; role: 'client' | 'admin' } | null>(null);
  const [userInfo, setUserInfo] = useState<ApiInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme = prefersDark ? 'dark' : 'light';
      setTheme(initialTheme);
      document.documentElement.setAttribute('data-theme', initialTheme);
    }

    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setIsLoggedIn(true);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
      }
    }

    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const apiResponse: ApiInfo = {
        name: "Murtesa Developer",
        email: "murtesa.developer@proton.me",
        phone: "+252 63 8266449",
        links: {
          github: "https://github.com/Murtesa-developer",
          freelancer: "https://www.freelancer.com/u/murtesa",
          linkedin: "https://www.linkedin.com/in/murtesa-developer-587628325/",
          discord: {
            username: "murtesa_dev",
            profile: "https://discord.com/users/1058737274646437979"
          }
        }
      };
      
      setUserInfo(apiResponse);
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUserInfo({
        name: "Murtesa Developer",
        email: "murtesa.developer@proton.me",
        phone: "+252 63 8266449",
        links: {
          github: "https://github.com/Murtesa-developer",
          freelancer: "https://www.freelancer.com/u/murtesa",
          linkedin: "https://www.linkedin.com/in/murtesa-developer-587628325/",
          discord: {
            username: "murtesa_dev",
            profile: "https://discord.com/users/1058737274646437979"
          }
        }
      });
    } finally {
      setLoading(false);
      setIsCheckingAuth(false);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const handleLogin = (userData: { name: string; email: string; role: 'client' | 'admin' }) => {
    setIsLoggedIn(true);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    localStorage.removeItem('user');
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-primary transition-colors duration-300">
        <Routes>
          <Route path="/" element={
            <HomePage 
              theme={theme} 
              toggleTheme={toggleTheme} 
              isLoggedIn={isLoggedIn} 
              user={user} 
              userInfo={userInfo}
              loading={loading}
              onLogin={() => window.location.href = '/login'} 
              onLogout={handleLogout} 
            />
          } />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

interface HomePageProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  isLoggedIn: boolean;
  user: { name: string; email: string; role: 'client' | 'admin' } | null;
  userInfo: ApiInfo | null;
  loading: boolean;
  onLogin: () => void;
  onLogout: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ 
  theme, 
  toggleTheme, 
  isLoggedIn, 
  user, 
  userInfo, 
  loading,
  onLogin, 
  onLogout 
}) => {
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'skills', 'projects', 'resume', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-secondary">Loading information...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header 
        theme={theme} 
        toggleTheme={toggleTheme}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />
      
      <HomeHeroSection scrollToSection={scrollToSection} userInfo={userInfo} />
      <AboutSection userInfo={userInfo} />
      <SkillsSection />
      <ProjectsSection />
      <ResumeSection />
      <ContactSection userInfo={userInfo} />
      <Footer userInfo={userInfo} />
    </>
  );
};

interface HomeHeroSectionProps {
  scrollToSection: (sectionId: string) => void;
  userInfo: ApiInfo | null;
}

const HomeHeroSection: React.FC<HomeHeroSectionProps> = ({ scrollToSection, userInfo }) => {
  const discordUsername = userInfo?.links.discord.username || 'murtesa_dev';
  const discordProfile = userInfo?.links.discord.profile || 'https://discord.com/users/murtesa_dev';
  const freelancerUsername = userInfo?.links.freelancer.split('/').pop() || 'murtesa';
  const email = userInfo?.email || 'murtesa.developer@proton.me';
  const phone = userInfo?.phone || '+252 63 8266449';

  return (
    <section id="home" className="hero-section hero-home-section">
      <div className="hero-background">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      <div className="container">
        <div className="hero-content-layout">
          <div className="hero-left animate-slide-in-left">
            <div className="profile-container">
              <div className="profile-image-wrapper">
                <img 
                  src={avatar}
                  alt={userInfo?.name || "Murtesa Developer"}
                  className="hero-profile-image"
                />
                <div className="profile-frame"></div>
              </div>
              
              <div className="quick-stats">
                <div className="stat-item quick-stat">
                  <span className="stat-value">5+</span>
                  <span className="stat-label">Years Exp</span>
                </div>
                <div className="stat-item quick-stat">
                  <span className="stat-value">50+</span>
                  <span className="stat-label">Projects</span>
                </div>
                <div className="stat-item quick-stat">
                  <span className="stat-value">100%</span>
                  <span className="stat-label">Satisfaction</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-right animate-slide-in-right">
            <div className="hero-intro">
              <span className="greeting-text">👋 Hello, I'm</span>
              <h1 className="hero-name">{userInfo?.name || "Murtesa Developer"}</h1>
              <h2 className="hero-title">Full Stack Developer & Open Source Creator</h2>
              
              <div className="hero-description">
                <p>
                  I specialize in creating innovative open source solutions and modern web applications. 
                  With expertise in React, Node.js, and cross-platform development, I build tools that 
                  solve real problems for developers and users worldwide.
                </p>
                <p className="mt-4">
                  Passionate about clean code, user-centered design, and creating utilities that make 
                  development easier and more enjoyable.
                </p>
              </div>

              <div className="key-highlights">
                <div className="highlight-item">
                  <svg className="highlight-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>5+ Years Professional Experience</span>
                </div>
                <div className="highlight-item">
                  <svg className="highlight-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Open Source Contributor</span>
                </div>
                <div className="highlight-item">
                  <svg className="highlight-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Cross-Platform Specialist</span>
                </div>
              </div>

              <div className="contact-info-mini">
                <div className="contact-item">
                  <svg className="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{userInfo?.email}</span>
                </div>
                <div className="contact-item">
                  <svg className="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>{userInfo?.phone}</span>
                </div>
                <div className="contact-item">
                  <svg className="contact-icon discord-svg" viewBox="0 0 127.14 96.36" fill="currentColor">
                    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                  </svg>
                  <span className="dark:text-white text-black">
                    {userInfo?.links.discord.username}
                  </span>
                </div>
                <div className="contact-item">
                  <svg className="contact-icon freelancer-icon" viewBox="0 0 122.88 91.38" fill="#29b2fe">
                    <path d="M72.19,0l8.37,11.74L122.88,0Zm-44,91.38L51.08,69,37.3,54.19,28.18,91.38ZM68.7,0,56.46,11l20.59.77L68.7,0ZM21.11,0l4.42,9,24.23,1.51L21.11,0Zm13,46.73L52,13.33,0,10.52,34.09,46.73Zm2.08,2.11L53.08,67,71.73,48.72l5.79-34L54.79,13.51,36.17,48.84Zm0,0Z"/>
                  </svg>
                  <a 
                    href={userInfo?.links.freelancer} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="dark:text-white text-black hover:text-accent transition-colors !important"
                    style={{ color: 'inherit' }}
                  >
                    Freelancer Profile
                  </a>
                </div>
              </div>

              <div className="hero-actions">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="button button-primary"
                >
                  <span>Start a Project</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
                <button 
                  onClick={() => scrollToSection('projects')}
                  className="button button-secondary"
                >
                  View Projects
                </button>
              </div>

              <div className="hero-social">
                <span className="social-label">Connect with me:</span>
                <div className="social-icons">
                  <a href={userInfo?.links.github || "https://github.com/Murtesa-developer"} target="_blank" rel="noopener noreferrer" className="social-icon github-icon" title="GitHub">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                  <a href={userInfo?.links.linkedin || "https://linkedin.com/in/murtesa-developer"} target="_blank" rel="noopener noreferrer" className="social-icon linkedin-icon" title="LinkedIn">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </a>
                  <a href={discordProfile} target="_blank" rel="noopener noreferrer" className="social-icon discord-icon" title="Discord">
                    <svg className="w-5 h-5 discord-svg" viewBox="0 0 127.14 96.36" fill="currentColor">
                      <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                    </svg>
                  </a>
                  <a href={userInfo?.links.freelancer || "https://www.freelancer.com/u/murtesa"} target="_blank" rel="noopener noreferrer" className="social-icon freelancer-icon" title="Freelancer.com">
                    <svg className="w-5 h-5 freelancer-svg" viewBox="0 0 122.88 91.38" fill="#29b2fe">
                      <path d="M72.19,0l8.37,11.74L122.88,0Zm-44,91.38L51.08,69,37.3,54.19,28.18,91.38ZM68.7,0,56.46,11l20.59.77L68.7,0ZM21.11,0l4.42,9,24.23,1.51L21.11,0Zm13,46.73L52,13.33,0,10.52,34.09,46.73Zm2.08,2.11L53.08,67,71.73,48.72l5.79-34L54.79,13.51,36.17,48.84Zm0,0Z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface AboutSectionProps {
  userInfo: ApiInfo | null;
}

const AboutSection: React.FC<AboutSectionProps> = ({ userInfo }) => {
  const aboutContent = {
    introduction: `I'm ${userInfo?.name || "Murtesa Developer"}, a passionate Full Stack Developer and open source creator with over 2022 years of experience building innovative tools and web applications. My journey in technology is driven by creating solutions that make developers' lives easier.`,
    
    philosophy: "I believe in creating software that solves real problems. My approach combines technical expertise with practical utility, always focusing on building tools that are both powerful and easy to use. Open source is at the heart of my development philosophy.",
    
    expertise: [
      "Full Stack Development with modern JavaScript/TypeScript, Kotlin/java, and Python",
      "Cross-platform desktop and mobile applications",
      "Open source tool development",
      "API design and integration",
      "Real-time notification systems",
      "VPN management solutions"
    ],
    
    currentFocus: "Currently, I'm focused on building useful open source tools like Murgit, Mursync, and Murawake. I'm always excited about new projects that challenge me and help the developer community.",
    
    personalInterests: [
      "Creating open source utilities",
      "Learning new technologies",
      "Building cross-platform solutions",
      "Contributing to developer tools",
      "Solving practical problems with code"
    ]
  };

  return (
    <section id="about" className="about-section section-padding bg-secondary">
      <div className="container">
        <h2 className="section-title">About Me</h2>
        
        <div className="about-content-new">
          <div className="about-block card-3d animate-slide-in-up">
            <h3 className="about-subtitle">Who I Am</h3>
            <p className="about-text">{aboutContent.introduction}</p>
          </div>

          <div className="about-block card-3d animate-slide-in-up" style={{ animationDelay: '100ms' }}>
            <h3 className="about-subtitle">My Philosophy</h3>
            <p className="about-text">{aboutContent.philosophy}</p>
          </div>

          <div className="about-block card-3d animate-slide-in-up" style={{ animationDelay: '200ms' }}>
            <h3 className="about-subtitle">Areas of Expertise</h3>
            <div className="expertise-grid">
              {aboutContent.expertise.map((item, index) => (
                <div key={index} className="expertise-item">
                  <svg className="expertise-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="about-block card-3d animate-slide-in-up" style={{ animationDelay: '300ms' }}>
            <h3 className="about-subtitle">Current Focus</h3>
            <p className="about-text">{aboutContent.currentFocus}</p>
          </div>

          <div className="about-block card-3d animate-slide-in-up" style={{ animationDelay: '400ms' }}>
            <h3 className="about-subtitle">Beyond Coding</h3>
            <div className="interests-grid">
              {aboutContent.personalInterests.map((interest, index) => (
                <div key={index} className="interest-item">
                  <span className="interest-text">{interest}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SkillsSection: React.FC = () => {
  const skillCategories = [
    {
      title: "Frontend",
      skills: [
        { name: "React", level: 95 },
        { name: "TypeScript", level: 90 },
        { name: "Next.js", level: 85 },
        { name: "Tailwind CSS", level: 90 }
      ]
    },
    {
      title: "Backend",
      skills: [
        { name: "Node.js", level: 88 },
        { name: "Python", level: 80 },
        { name: "GraphQL", level: 85 },
        { name: "REST APIs", level: 92 }
      ]
    },
    {
      title: "Tools & Others",
      skills: [
        { name: "Git & GitHub", level: 95 },
        { name: "Docker", level: 80 },
        { name: "AWS", level: 75 },
        { name: "MongoDB", level: 85 }
      ]
    }
  ];

  return (
    <section id="skills" className="skills-section section-padding">
      <div className="container">
        <h2 className="section-title">Technical Expertise</h2>
        <div className="skills-categories">
          {skillCategories.map((category, index) => (
            <div key={index} className="skill-category card-3d animate-slide-in-up" style={{ animationDelay: `${index * 200}ms` }}>
              <h3 className="category-title">{category.title}</h3>
              <div className="skill-list">
                {category.skills.map((skill, skillIndex) => (
                  <div key={skillIndex} className="skill">
                    <span className="skill-name">{skill.name}</span>
                    <div className="skill-bar">
                      <div 
                        className="skill-level" 
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

interface ProjectDetailModalProps {
  project: any;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({ project, isOpen, onClose }) => {
  if (!isOpen || !project) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content project-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{project.title}</h3>
          <button className="modal-close" onClick={onClose}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="modal-body">
          <div className="project-modal-image">
            <img src={project.image} alt={project.title} />
          </div>
          
          <div className="project-modal-info">
            <div className="project-modal-platform">
              <span className={`platform-badge ${project.platform === 'Open Source' ? 'platform-freelancer' : 'platform-direct'}`}>
                {project.platform}
              </span>
              <div className="project-meta-info">
                <span className="meta-info-item">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {project.duration}
                </span>
                <span className="meta-info-item">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {project.teamSize}
                </span>
              </div>
            </div>
            
            <div className="project-modal-description">
              <h4>Description</h4>
              <p>{project.detailedDescription}</p>
            </div>
            
            <div className="project-modal-grid">
              <div className="project-modal-section">
                <h4>Challenges</h4>
                <p>{project.challenges}</p>
              </div>
              
              <div className="project-modal-section">
                <h4>Solutions</h4>
                <p>{project.solutions}</p>
              </div>
            </div>
            
            <div className="project-modal-section">
              <h4>Technologies Used</h4>
              <div className="technologies-grid">
                {project.technologies.map((tech: string, index: number) => (
                  <span key={index} className="technology-tag">{tech}</span>
                ))}
              </div>
            </div>
            
            <div className="project-modal-section">
              <h4>Key Features</h4>
              <ul className="features-list">
                {project.features.map((feature: string, index: number) => (
                  <li key={index}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="project-modal-links">
              <a href={project.githubLink} className="button button-secondary" target="_blank" rel="noopener noreferrer">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                Source Code
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectsSection: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openProjectModal = (project: any) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeProjectModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const projects = [
    {
      title: "Murgit",
      description: "A helpful tool for GitHub users to receive notifications about repository activity and updates.",
      detailedDescription: "Murgit is a powerful notification system for GitHub users that helps you stay updated with repository activity. It monitors your repositories and sends real-time notifications about issues, pull requests, commits, and more. Perfect for developers who want to stay on top of their projects without constantly checking GitHub.",
      image: "assets/murgit.png",
      tags: ["GitHub API", "Real-time", "Notifications"],
      githubLink: "https://github.com/Murtesa-developer/murgit",
      platform: "Open Source",
      teamSize: "Solo Project",
      duration: "3 Months",
      challenges: "Implementing reliable webhook handling and real-time notification delivery",
      solutions: "Used GitHub's webhook system with a queuing mechanism for reliable delivery",
      technologies: ["Kotlin", "GitHub API"],
      features: [
        "Real-time GitHub notifications",
        "Issue and PR tracking",
        "Commit monitoring",
        "Custom notification filters",
        "Desktop notifications",
        "Multi-repository support"
      ]
    },
    {
      title: "Mursync",
      description: "A tool that helps communicate between mobile and desktop devices easily with unique features.",
      detailedDescription: "Mursync is a cross-platform communication tool that enables seamless interaction between mobile and desktop devices. Unlike traditional solutions, Mursync focuses on unique features that solve real problems: clipboard synchronization, file transfer, remote commands, and notification mirroring. The Android app and desktop client work together to create a unified experience across your devices.",
      image: "assets/mursync.png",
      tags: ["Kotlin", "c++"],
      githubLink: "https://github.com/Murtesa-developer/mursync-android",
      platform: "Open Source",
      teamSize: "Solo Project",
      duration: "1 week",
      challenges: "Creating reliable cross-platform communication and handling device discovery",
      solutions: "Implemented WebSocket with local network discovery and encrypted communication",
      technologies: ["Kotlin", "c++"],
      features: [
        "Cross-platform clipboard sync",
        "File transfer between devices",
        "Remote command execution",
        "Notification mirroring",
        "Device discovery",
        "End-to-end encryption",
        "Low latency communication"
      ]
    },
    {
      title: "Murawake",
      description: "A simple but powerful app that prevents screen sleep while reading or working.",
      detailedDescription: "Murawake solves a common problem: when you're reading something important, your screen goes to sleep. This app lets you press a button to keep your screen awake indefinitely. Perfect for reading documentation, books, articles, or any situation where you need your screen to stay on. Simple, lightweight, and effective.",
      image: "assets/murawake.png",
      tags: ["kotlin"],
      githubLink: "https://github.com/Murtesa-developer/murawake",
      platform: "Open Source",
      teamSize: "Solo Project",
      duration: "1 Day",
      challenges: "Handling system power management APIs across different operating systems",
      solutions: "Used Kotlin powerSaveBlocker API with cross-platform compatibility",
      technologies: ["Kotlin", "System APIs"],
      features: [
        "One-click screen awake",
        "System tray integration",
        "Lightweight memory usage",
        "Cross-platform support",
        "Keyboard shortcuts",
        "Auto-off timer option"
      ]
    },
    {
      title: "Murgate - VPN Manager",
      description: "Enterprise VPN management solution for managing WG-Easy and multiple nodes.",
      detailedDescription: "Murgate is a comprehensive VPN management platform designed for businesses and organizations. It helps you manage employees or team members who need access to VPN software like WG-Easy. Generate QR codes for easy connection, manage multiple nodes simultaneously, and control access for unlimited users. Features include real-time node monitoring, bandwidth tracking, access logs, and automated configuration. Future updates will include advanced analytics, custom routing rules, and API access for automation. This is a premium project that comes with full support and regular updates.",
      image: "assets/murgate.png",
      tags: ["VPN", "WG-Easy", "Node Management", "Enterprise", "Admin Panel"],
      platform: "Premium Project",
      teamSize: "Solo Project",
      duration: "1 Week",
      challenges: "Managing multiple VPN nodes and ensuring seamless QR code generation for clients",
      solutions: "Built a centralized management system with WireGuard API integration and real-time sync",
      technologies: ["React", "Node.js", "WireGuard API", "PostgreSQL", "Docker", "WebSocket"],
      features: [
        "Multi-node VPN management",
        "QR code generation for easy connection",
        "Employee access management",
        "Real-time node monitoring",
        "Bandwidth tracking per user",
        "Access logs and audit trails",
        "Automated node configuration",
        "Upcoming: Advanced analytics",
        "Upcoming: Custom routing rules",
        "Upcoming: API access for automation"
      ]
    }
  ];

  return (
    <>
      <section id="projects" className="section-padding bg-tertiary">
        <div className="container">
          <h2 className="section-title">Featured Projects</h2>
          <p className="text-center text-secondary mb-8 max-w-2xl mx-auto">
            Click "View Details" on any project to see comprehensive information including features, technologies used, and source code links.
          </p>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <div key={index} className="project-card card-3d animate-slide-in-up" style={{ animationDelay: `${index * 200}ms` }}>
                <img src={project.image} alt={project.title} className="project-image" />
                <div className="project-content">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="project-title">{project.title}</h3>
                    <span className={`platform-badge ${project.platform === 'Open Source' ? 'platform-freelancer' : 'platform-direct'}`}>
                      {project.platform === 'Open Source' ? (
                        <>
                          <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                          </svg>
                          Open Source
                        </>
                      ) : 'Premium'}
                    </span>
                  </div>
                  <p className="project-description">{project.description}</p>
                  
                  <div className="project-tags">
                    {project.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="project-tag">{tag}</span>
                    ))}
                  </div>
                  
                  <div className="project-links">
                    <a href={project.githubLink} className="project-link" target="_blank" rel="noopener noreferrer">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                      Code
                    </a>
                    <button 
                      onClick={() => openProjectModal(project)}
                      className="project-link view-details-btn"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProjectDetailModal 
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={closeProjectModal}
      />
    </>
  );
};


const ResumeSection: React.FC = () => {
  const experiences = [
    {
      title: "Full Stack Developer",
      company: "Euranix Technologies Private Limited.",
      period: "2025 - Present",
      description: "to become developer in building scalable web applications for enterprise clients.",
      achievements: ["understanding what people want"]
    },
    {
      title: "Full stack freelancer",
      company: "self employed",
      period: "2025 - Present",
      description: "Developed responsive web applications using React and modern frontend technologies.",
      achievements: ["Improved my communication skills and attracting clients", "Improved backend development scores by 90%"]
    },
    {
      title: "Discord bot Developer",
      company: "self employed",
      period: "2024 - 2026",
      description: "Developed custom Discord bots for various servers and communities.",
      achievements: ["Created bots that have backup feature", "Implemented advanced moderation features", "Built custom slash commands"]
    },
    {
      title: "Game Client Developer",
      company: "self employed",
      period: "2023 - 2025",
      description: "Built custom minecraft clients.",
      achievements: ["maked best minecraft client"]
    }
  ];

  return (
    <section id="resume" className="resume-section section-padding bg-tertiary">
      <div className="container">
        <h2 className="section-title">Experience & Education</h2>
        <div className="resume-container">
          <div className="mb-5">
            <h3 className="text-center mb-4">Work Experience</h3>
            <div className="space-y-4">
              {experiences.map((exp, index) => (
                <div key={index} className="card-3d animate-slide-in-up" style={{ animationDelay: `${index * 200}ms` }}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-accent font-bold">{exp.title}</h4>
                      <p className="font-semibold">{exp.company}</p>
                    </div>
                    <span className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-semibold">
                      {exp.period}
                    </span>
                  </div>
                  <p className="text-secondary mb-3">{exp.description}</p>
                  <ul className="list-disc list-inside space-y-1">
                    {exp.achievements.map((achievement, idx) => (
                      <li key={idx} className="text-sm">{achievement}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <h3 className="text-center mb-4">Education</h3>
            <div className="space-y-4">
              <div className="card-3d animate-slide-in-up">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-accent font-bold">Self Learner</h4>
                    <p className="font-semibold">Continuous Learning & Open Source Contribution</p>
                    <p className="text-secondary text-sm mt-2">Passionate about self-directed learning through building real-world projects, contributing to open source, and staying current with emerging technologies. Constantly exploring new frameworks, tools, and best practices through hands-on development.</p>
                  </div>
                  <span className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-semibold">
                    2023 - Present
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface ContactSectionProps {
  userInfo: ApiInfo | null;
}

const ContactSection: React.FC<ContactSectionProps> = ({ userInfo }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Form submitted:', formData);
    setSubmitStatus('success');
    
    setFormData({ name: '', email: '', message: '' });
    setIsSubmitting(false);
    
    setTimeout(() => setSubmitStatus('idle'), 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const discordProfile = userInfo?.links.discord.profile || "https://discord.com/users/murtesa_dev";
  const discordUsername = userInfo?.links.discord.username || "murtesa_dev";

  const contactMethods = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "Email",
      value: userInfo?.email || "murtesa.developer@proton.me",
      link: `mailto:${userInfo?.email || "murtesa.developer@proton.me"}`,
      description: "Fast response within 48 hours"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: "Phone",
      value: userInfo?.phone || "+252 63 8266449",
      link: `tel:${userInfo?.phone?.replace(/\s+/g, '') || "+252638266449"}`,
      description: "Available saturday - wednesday, 4PM-9PM  GMT +3"
    },
    {
      icon: (
        <svg className="w-6 h-6 discord-svg" viewBox="0 0 127.14 96.36" fill="currentColor">
          <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
        </svg>
      ),
      title: "Discord",
      value: discordUsername,
      link: discordProfile,
      description: "Connect for instant chat"
    },
    {
      icon: (
        <svg className="w-6 h-6 freelancer-svg" viewBox="0 0 122.88 91.38" fill="#29b2fe">
          <path d="M72.19,0l8.37,11.74L122.88,0Zm-44,91.38L51.08,69,37.3,54.19,28.18,91.38ZM68.7,0,56.46,11l20.59.77L68.7,0ZM21.11,0l4.42,9,24.23,1.51L21.11,0Zm13,46.73L52,13.33,0,10.52,34.09,46.73Zm2.08,2.11L53.08,67,71.73,48.72l5.79-34L54.79,13.51,36.17,48.84Zm0,0Z"/>
        </svg>
      ),
      title: "Freelancer.com",
      value: `@${userInfo?.links.freelancer.split('/').pop() || "murtesa"}`,
      link: userInfo?.links.freelancer || "https://www.freelancer.com/u/murtesa",
      description: "Hire me directly"
    }
  ];

  return (
    <section id="contact" className="section-padding bg-secondary">
      <div className="container">
        <h2 className="section-title">Let's Work Together</h2>
        <p className="text-center text-secondary mb-8 max-w-2xl mx-auto">
          Get in touch through any of the following channels. I typically respond within a few hours.
        </p>
        
          <div className="contact-methods">
            {contactMethods.map((method, index) => (
              <a 
                key={index}
                href={method.link}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-method-card card-3d animate-slide-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="contact-method-icon">
                  {method.icon}
                </div>
                <div className="contact-method-content">
                  <h4 className="contact-method-title">{method.title}</h4>
                  <p className="contact-method-value">{method.value}</p>
                  <p className="contact-method-description">{method.description}</p>
                </div>
              </a>
            ))}
          </div>
          
      </div>
    </section>
  );
};

interface FooterProps {
  userInfo: ApiInfo | null;
}

const Footer: React.FC<FooterProps> = ({ userInfo }) => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: "GitHub",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      ),
      url: userInfo?.links.github || "https://github.com/Murtesa-developer"
    },
    {
      name: "LinkedIn",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
        </svg>
      ),
      url: userInfo?.links.linkedin || "https://www.linkedin.com/in/murtesa-developer-587628325/"
    },
    {
      name: "Discord",
      icon: (
        <svg className="w-5 h-5 discord-svg" viewBox="0 0 127.14 96.36" fill="currentColor">
          <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
        </svg>
      ),
      url: userInfo?.links.discord.profile || "https://discord.com/users/murtesa_dev"
    },
    {
      name: "Freelancer.com",
      icon: (
        <svg className="w-5 h-5 freelancer-svg" viewBox="0 0 122.88 91.38" fill="#29b2fe">
          <path d="M72.19,0l8.37,11.74L122.88,0Zm-44,91.38L51.08,69,37.3,54.19,28.18,91.38ZM68.7,0,56.46,11l20.59.77L68.7,0ZM21.11,0l4.42,9,24.23,1.51L21.11,0Zm13,46.73L52,13.33,0,10.52,34.09,46.73Zm2.08,2.11L53.08,67,71.73,48.72l5.79-34L54.79,13.51,36.17,48.84Zm0,0Z"/>
        </svg>
      ),
      url: userInfo?.links.freelancer || "https://www.freelancer.com/u/murtesa"
    }
  ];

  return (
    <footer className="footer bg-tertiary">
      <div className="container">
        <div className="footer-content">
          <div className="social-links">
            {socialLinks.map((social, index) => (
              <a 
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`social-link ${social.name.toLowerCase()}-icon`}
                aria-label={social.name}
                title={social.name}
              >
                {social.icon}
              </a>
            ))}
          </div>
          
          <p className="copyright text-muted">
            © {currentYear} {userInfo?.name || "Murtesa Developer"}. All rights reserved.
          </p>
          
          <div className="footer-links mt-4">
            <a href="#home" className="footer-link">Home</a>
            <a href="#about" className="footer-link">About</a>
            <a href="#projects" className="footer-link">Projects</a>
            <a href="#contact" className="footer-link">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default App;